import type { BlobView, HoldView } from "@envstash/core-wasm";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CameraIcon, DownloadIcon, Undo2Icon } from "lucide-react";
import { useState } from "react";
import { DiffView } from "@/components/diff-view";
import { EmptyState, ErrorText, PageHeader } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { downloadBytes, loadBlobVersion } from "@/lib/blobs";
import { formatBytes, utf8Decode } from "@/lib/bytes";
import { fmtDateTime } from "@/lib/format";
import { useDeviceNames, useVault } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import { vaultStore } from "@/lib/vault-store";

export const Route = createFileRoute("/_app/files/$fileId")({ component: FilePage });

function useVersion(fileId: string, hash: string | null) {
  return useQuery({
    queryKey: ["blob", fileId, hash],
    enabled: hash !== null,
    queryFn: async () => {
      const vault = vaultStore.vault;
      if (!vault || !hash) return null;
      return loadBlobVersion(vault, fileId, hash);
    },
  });
}

function FilePage() {
  const { fileId } = Route.useParams();
  const v = useVault();
  const deviceName = useDeviceNames();
  const file = v.snapshot?.files.find((f) => f.id === fileId);
  const project = v.snapshot?.projects.find((p) => p.id === file?.project_id);
  const [selected, setSelected] = useState<string | null>(null);
  const [compare, setCompare] = useState<string | null>(null);
  const versions = file?.blob_history ?? [];
  const snapshots: HoldView[] = file?.holds ?? [];
  const current = selected ?? versions[0]?.content_hash ?? null;
  const base = compare ?? versions.find((x) => x.content_hash !== current)?.content_hash ?? null;
  const a = useVersion(fileId, base);
  const b = useVersion(fileId, current);
  if (!v.snapshot) return null;
  if (!file) return <EmptyState title="File not found" />;
  const aText = a.data ? utf8Decode(a.data) : null;
  const bText = b.data ? utf8Decode(b.data) : null;
  const name = file.rel_path.split("/").pop() ?? "file";

  const Version = ({ ver, role, note }: { ver: BlobView; role: "current" | "base" | null; note?: string }) => (
    <button
      type="button"
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-accent/60",
        role === "current" && "bg-accent",
        role === "base" && "bg-muted",
      )}
      onClick={(e) => {
        if (e.shiftKey || e.metaKey) setCompare(ver.content_hash);
        else setSelected(ver.content_hash);
      }}
      title="Click to view · shift-click to compare against"
    >
      <span className="min-w-0">
        <span className="block truncate font-mono text-xs">{ver.content_hash.slice(0, 12)}</span>
        <span className="block text-[11px] text-muted-foreground">
          {deviceName(ver.hlc.device_id)} · {fmtDateTime(ver.hlc.wall_ms)} · {formatBytes(ver.size)}
        </span>
        {note ? <span className="block truncate font-mono text-[11px] text-muted-foreground">{note}</span> : null}
      </span>
      {role ? (
        <Badge variant="outline" className="shrink-0">
          {role === "current" ? "viewing" : "base"}
        </Badge>
      ) : null}
    </button>
  );

  return (
    <>
      <PageHeader
        title={file.rel_path}
        description={
          <>
            {project?.name ?? "?"} · {file.mode} · {versions.length} version{versions.length === 1 ? "" : "s"}
            {file.blob_conflicts.length > 0 ? (
              <>
                {" "}
                · <span className="text-destructive">conflicting versions exist; the latest wins on disk</span>
              </>
            ) : null}
          </>
        }
        actions={
          <>
            {current && current !== file.blob_current?.content_hash && b.data ? (
              <Button
                variant="outline"
                size="sm"
                data-testid="make-current"
                onClick={() => {
                  const op = vaultStore.vault?.blobRevertOp(file.id, current);
                  if (op) void vaultStore.commit([op]);
                  setSelected(null);
                }}
              >
                <Undo2Icon /> Make this the current version
              </Button>
            ) : null}
            {b.data ? (
              <Button variant="outline" size="sm" onClick={() => downloadBytes(name, b.data as Uint8Array)}>
                <DownloadIcon /> Download viewed version
              </Button>
            ) : null}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Versions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 p-2">
            {versions.map((ver) => (
              <Version
                key={`${ver.hlc.wall_ms}-${ver.content_hash}`}
                ver={ver}
                role={ver.content_hash === current ? "current" : ver.content_hash === base ? "base" : null}
              />
            ))}
            {snapshots.length > 0 ? (
              <>
                <p className="flex items-center gap-1 px-2 pt-3 text-xs font-medium">
                  <CameraIcon className="size-3.5" /> Snapshots
                </p>
                <p className="px-2 pb-1 text-[11px] text-muted-foreground">
                  Copies found on a Mac that differed from the vault. They never win on their own.
                </p>
                {snapshots.map((s) => (
                  <Version
                    key={`${s.hlc.wall_ms}-${s.content_hash}-${s.device_id}`}
                    ver={{ hlc: s.hlc, content_hash: s.content_hash, size: s.size }}
                    role={s.content_hash === current ? "current" : s.content_hash === base ? "base" : null}
                    note={`${s.local_path}${s.resolution === null ? " · undecided" : ""}`}
                  />
                ))}
              </>
            ) : null}
            <p className="px-2 pt-2 text-[11px] text-muted-foreground">
              Click a version to view it; shift-click another to diff against it.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              {base && base !== current ? "Changes since the base version" : "Content"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ErrorText error={a.error ?? b.error} />
            {b.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
            {b.data && bText === null ? (
              <p className="text-sm text-muted-foreground">
                Binary content ({formatBytes(b.data.length)}). Download to inspect.
              </p>
            ) : null}
            {bText !== null && base && base !== current ? (
              aText !== null ? (
                <DiffView before={aText} after={bText} />
              ) : (
                <p className="text-sm text-muted-foreground">Base version is binary.</p>
              )
            ) : null}
            {bText !== null && (!base || base === current) ? (
              <pre className="max-h-[60vh] overflow-auto rounded-lg border bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap">
                {bText}
              </pre>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
